import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import RadioGroup from '@mui/material/RadioGroup';
import Radio from '@mui/material/Radio';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import Checkbox from '@mui/material/Checkbox';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import validator from 'validator';
import { getAllDivisions, getAllCategories } from '../utils/scoring';
import CountrySelect from './CountrySelect';
import EquipmentBelt from './EquipmentBelt';
import * as api from '../api';

export const toDivisionKey = (div) => {
  const map = {
    'standard': 'standard', 'production': 'production', 'open': 'open',
    'production-optics': 'productionOptics', 'optics': 'optics', 'classic': 'classic',
    'revolver': 'revolver', 'pcc-optics': 'pccOptics', 'pcc-iron': 'pccIron',
    'sg-open': 'sgOpen', 'sg-modified': 'sgModified', 'sg-standard': 'sgStandard',
    'sg-standard-manual': 'sgStandardManual',
  };
  return map[div] || div;
};

export const toCategoryKey = (cat) => {
  const map = {
    'regular': 'regular', 'senior': 'senior', 'super-senior': 'superSenior',
    'grand-senior': 'grandSenior', 'lady-senior': 'ladySenior', 'junior': 'junior',
    'super-junior': 'superJunior', 'grand-junior': 'grandJunior', 'lady-junior': 'ladyJunior',
    'lady-super-junior': 'ladySuperJunior', 'lady-grand-junior': 'ladyGrandJunior', 'lady': 'lady',
  };
  return map[cat] || cat;
};

/**
 * Shooters currently in a squad, not counting the shooter being edited.
 * Uses server-side availability when given (public registration, where the
 * shooter list may be private), else counts the shooters list (manage mode).
 */
const squadOccupancy = (sq, { squadAvailability, shooters, editingShooter }) => {
  if (squadAvailability) {
    return (sq.taken || 0) - (editingShooter && editingShooter.squad === sq.id && !editingShooter.cancelledParticipation ? 1 : 0);
  }
  return shooters.filter((s) => s.squad === sq.id && (!editingShooter || s.id !== editingShooter.id)).length;
};

/**
 * Shared shooter form used by both public Registration and manage Shooters pages.
 *
 * Props:
 *   match        – match data (squads, teams, registrationExtraFields, type, etc.)
 *   shooters     – array of existing shooters (for squad counts & duplicate ID check)
 *   squadAvailability – squads with capacity from /squads/availability; when set, used
 *                  instead of match.squads + shooters, so squads needn't be published
 *   editingShooter – shooter object when editing, null when creating
 *   onSave(data) – called with form data; parent handles API call
 *   saving       – boolean, disables submit button
 *   showTos      – show Terms of Service checkbox (default true)
 *   idLocked     – disable country code + shooter ID fields
 *   prefill      – { name, email } to pre-fill for new registrations
 *   submitLabel  – custom label for submit button
 *   noPaper      – don't wrap in Paper (for dialog usage)
 *   beforeSubmit – ReactNode to render before the submit button (e.g. reason, notify checkbox)
 */
const ShooterForm = forwardRef(function ShooterForm({
  match, shooters = [], editingShooter, onSave, saving,
  showTos = true, idLocked = false, prefill, submitLabel, noPaper = false,
  beforeSubmit, gsheetActive = false, ro = false, squadAvailability = null,
}, ref) {
  const { t } = useTranslation();

  const [publicId, setPublicId] = useState('');
  const [countryCode, setCountryCode] = useState('');
  const [name, setName] = useState('');
  const [club, setClub] = useState('');
  const [email, setEmail] = useState('');
  const [division, setDivision] = useState('standard');
  const [category, setCategory] = useState('regular');
  const [powerFactor, setPowerFactor] = useState('minor');
  const [squad, setSquad] = useState(null);
  const [team, setTeam] = useState(null);
  const [extraFields, setExtraFields] = useState({});
  const [acceptTos, setAcceptTos] = useState(!showTos);
  const [errors, setErrors] = useState({});
  const [gsheetData, setGsheetData] = useState(null); // { name, club, limitations }
  const [gsheetChecking, setGsheetChecking] = useState(false);
  const [gsheetDisabledDivisions, setGsheetDisabledDivisions] = useState([]);

  const loadShooterData = (shooter) => {
    const parts = (shooter.publicId || '').split('|');
    if (parts.length > 1) {
      setCountryCode(parts[0]);
      setPublicId(parts.slice(1).join('|'));
    } else {
      setPublicId(shooter.publicId || '');
    }
    setName(shooter.name || '');
    setClub(shooter.club || '');
    if (shooter.email) setEmail(shooter.email);
    setDivision(shooter.division || 'standard');
    setCategory(shooter.category || 'regular');
    setPowerFactor(shooter['power-factor'] || 'minor');
    setSquad(shooter.squad || null);
    setTeam(shooter.team || null);
    if (shooter.extraFields) setExtraFields(shooter.extraFields);
  };

  // Expose loadData for parent (e.g. "load from past registration")
  useImperativeHandle(ref, () => ({
    loadData: loadShooterData,
  }));

  const handleGsheetLookup = async (cc, id) => {
    if (!gsheetActive || !id || !match) return;
    const fullId = cc ? `${cc}|${id}` : id;
    setGsheetChecking(true);
    setGsheetData(null);
    setGsheetDisabledDivisions([]);
    try {
      const res = await api.checkShooterId(match.id, fullId);
      const { gsheet } = res.data || {};
      if (gsheet) {
        setGsheetData(gsheet);
        setClub(gsheet.club || '');
        // Parse limitations for disabled divisions
        const lim = (gsheet.limitations || '').toUpperCase();
        const allDivs = getAllDivisions({ matchType: match.type });
        const disabled = allDivs.filter((d) => lim.indexOf('NO_' + d.toUpperCase()) > -1);
        setGsheetDisabledDivisions(disabled);
        setErrors((prev) => { const next = { ...prev }; delete next.gsheet; return next; });
      } else {
        setErrors((prev) => ({ ...prev, gsheet: t('registration.notInAuthorizedList', 'Shooter not found in authorized list') }));
      }
    } catch {
      setErrors((prev) => ({ ...prev, gsheet: t('registration.notInAuthorizedList', 'Shooter not found in authorized list') }));
    } finally {
      setGsheetChecking(false);
    }
  };

  useEffect(() => {
    if (!match) return;

    setCountryCode(match.countryCode || match.region || 'US');
    if (match.type === 'ipsc-shotgun') setPowerFactor('major');

    if (editingShooter) {
      loadShooterData(editingShooter);
      if (showTos) setAcceptTos(true);
      // Trigger GSheet lookup for existing shooter
      if (gsheetActive && editingShooter.publicId) {
        const parts = (editingShooter.publicId || '').split('|');
        const cc = parts.length > 1 ? parts[0] : match.countryCode || match.region || '';
        const num = parts.length > 1 ? parts.slice(1).join('|') : editingShooter.publicId;
        handleGsheetLookup(cc, num);
      }
    } else if (prefill) {
      if (prefill.name) setName(prefill.name);
      if (prefill.email) setEmail(prefill.email);
    }
  }, [match?.id, editingShooter?.id]);

  const validate = () => {
    const newErrors = {};

    if (!publicId.trim()) newErrors.publicId = 'Required';
    if (!name.trim()) newErrors.name = 'Required';
    if (email && !validator.isEmail(email)) newErrors.email = t('auth.emailError');
    if (!division) newErrors.division = 'Required';
    if (!category) newErrors.category = 'Required';
    if (showTos && !acceptTos) newErrors.tos = t('regLanding.tosRequired');
    if (gsheetActive && !gsheetData && !editingShooter) newErrors.gsheet = t('registration.notInAuthorizedList', 'Shooter not found in authorized list');

    if (!editingShooter && publicId && shooters) {
      const fullPublicId = countryCode ? `${countryCode}|${publicId}` : publicId;
      if (shooters.find((s) => s.publicId === fullPublicId)) {
        newErrors.publicId = t('registration.userExists');
      }
    }

    if (powerFactor === 'major' && ['production', 'production-optics', 'revolver', 'pcc-optics', 'pcc-iron'].includes(division)) {
      newErrors.powerFactor = t('registration.powerFactorError');
    }

    // In public registration mode, squad may be required
    if (showTos && match.squadSelection === 'during-registration' && !squad) {
      newErrors.squad = 'Required';
    }
    if (ro && !squad) {
      newErrors.squad = 'Required';
    }

    // Validate squad capacity
    if (squad && (shooters || squadAvailability)) {
      const selectedSquad = (squadAvailability || match.squads || []).find((sq) => sq.id === squad);
      if (selectedSquad && selectedSquad.maxShooters) {
        const shooterCount = squadOccupancy(selectedSquad, { squadAvailability, shooters, editingShooter });
        if (shooterCount >= selectedSquad.maxShooters) {
          newErrors.squad = t('registration.noPlacesLeft');
        }
      }
    }

    // Validate required extra fields (only in public registration mode)
    if (showTos) {
      for (const fieldKey of (match.registrationExtraFields || [])) {
        if (typeof fieldKey !== 'string') continue;
        const isOptional = fieldKey.endsWith('_optional');
        if (isOptional) continue;
        if (fieldKey === 'equipment') continue;
        if (!extraFields[fieldKey] || (typeof extraFields[fieldKey] === 'string' && !extraFields[fieldKey].trim())) {
          newErrors[`extra_${fieldKey}`] = 'Required';
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;

    const fullPublicId = countryCode ? `${countryCode}|${publicId}` : publicId;
    // Strip presigned URLs from image extra fields (unchanged images)
    const cleanedExtraFields = { ...extraFields };
    for (const [key, val] of Object.entries(cleanedExtraFields)) {
      if (typeof val === 'string' && val.startsWith('http')) {
        delete cleanedExtraFields[key];
      }
    }

    const data = {
      publicId: fullPublicId,
      name: name.trim(),
      club: gsheetActive ? null : (club.trim() || null),
      email: email.trim() || null,
      division,
      category,
      'power-factor': powerFactor,
      squad: squad || null,
      team: team || null,
      extraFields: cleanedExtraFields,
    };

    onSave(data);
  };

  if (!match) return null;

  const isManageMode = !showTos;
  const allDivisions = getAllDivisions({ matchType: match.type });
  const divisions = isManageMode ? allDivisions : allDivisions.filter((d) => !(match.registrationExcludeDivisions || []).includes(d));
  const categories = getAllCategories({ matchType: match.type });
  // Availability comes pre-filtered by the server (RO squads for RO registration)
  const squads = squadAvailability
    || (match.squads || []).filter((sq) => (isManageMode ? true : ro ? !!sq.ro : !sq.ro));
  const teams = match.teams || [];
  const showSquadSelection = ro
    || ((isManageMode || ((squadAvailability || match.squadsPublished) && match.squadSelection !== 'none' && match.squadSelection !== 'closed')) && squads.length > 0);
  const squadRequired = ro || (!isManageMode && match.squadSelection === 'during-registration');
  const showTeamSelection = teams.length > 0;
  const extraFieldsDef = match.registrationExtraFields || [];

  const tosText = t('registration.termsOfUse');
  const tosLabel = tosText.includes('###') ? (
    <Typography variant="body2">
      {tosText.split('###').map((part, i) => {
        if (i === 1) return <a key={i} href="https://docs.google.com/document/d/1Saeek_HSNjO4_yW6FvtkQHOAFplhmxEs1Ctd-UMYOto/edit?usp=sharing" target="_blank" rel="noopener noreferrer">{part}</a>;
        if (i === 3) return <a key={i} href="/privacy.html" target="_blank" rel="noopener noreferrer">{part}</a>;
        return <span key={i}>{part}</span>;
      })}
    </Typography>
  ) : <Typography variant="body2">{tosText}</Typography>;

  const Wrapper = noPaper ? Box : Paper;

  const formContent = (
    <Wrapper sx={noPaper ? { display: 'flex', flexDirection: 'column' } : { p: 3 }}>
      {/* Shooter ID */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <CountrySelect
          label={t('regLanding.countryCode')}
          value={countryCode}
          onChange={setCountryCode}
          disabled={idLocked}
          sx={{ minWidth: 180 }}
        />
        <TextField
          label={t('shooter.id')}
          value={publicId}
          onChange={(e) => setPublicId(e.target.value)}
          onBlur={() => { if (gsheetActive) handleGsheetLookup(countryCode, publicId); }}
          error={!!errors.publicId || !!errors.gsheet}
          helperText={errors.publicId || errors.gsheet}
          disabled={idLocked}
          fullWidth
          required
        />
      </Box>

      <TextField
        label={t('shooter.name')}
        value={name}
        onChange={(e) => setName(e.target.value)}
        error={!!errors.name}
        helperText={errors.name}
        fullWidth
        required
        sx={{ mb: 2 }}
      />

      <TextField
        label={t('shooter.club')}
        value={club}
        onChange={(e) => setClub(e.target.value)}
        fullWidth
        disabled={gsheetActive}
        sx={{ mb: 2 }}
      />

      <TextField
        label={t('shooter.email')}
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        error={!!errors.email}
        helperText={errors.email}
        fullWidth
        type="email"
        sx={{ mb: 2 }}
      />

      {/* Division */}
      <FormControl fullWidth sx={{ mb: 2 }} error={!!errors.division}>
        <InputLabel>{t('shooter.division')}</InputLabel>
        <Select value={division} label={t('shooter.division')} onChange={(e) => setDivision(e.target.value)}>
          {divisions.map((d) => (
            <MenuItem key={d} value={d} disabled={gsheetDisabledDivisions.includes(d)}>
              {t(`divisions.${toDivisionKey(d)}`, { defaultValue: d })}
            </MenuItem>
          ))}
        </Select>
        {errors.division && <FormHelperText>{errors.division}</FormHelperText>}
      </FormControl>

      {/* Category */}
      <FormControl fullWidth sx={{ mb: 2 }} error={!!errors.category}>
        <InputLabel>{t('shooter.category')}</InputLabel>
        <Select value={category} label={t('shooter.category')} onChange={(e) => setCategory(e.target.value)}>
          {categories.map((c) => (
            <MenuItem key={c} value={c}>
              {t(`categories.${toCategoryKey(c)}`, { defaultValue: c })}
            </MenuItem>
          ))}
        </Select>
        {errors.category && <FormHelperText>{errors.category}</FormHelperText>}
      </FormControl>

      {/* Power Factor */}
      {match.type !== 'ipsc-shotgun' && (
        <FormControl sx={{ mb: 2 }} error={!!errors.powerFactor}>
          <Typography variant="subtitle2" gutterBottom>{t('shooter.powerFactor')}</Typography>
          <RadioGroup row value={powerFactor} onChange={(e) => setPowerFactor(e.target.value)}>
            <FormControlLabel value="minor" control={<Radio />} label={t('registration.minor')} />
            <FormControlLabel value="major" control={<Radio />} label={t('registration.major')} />
          </RadioGroup>
          {errors.powerFactor && <FormHelperText>{errors.powerFactor}</FormHelperText>}
        </FormControl>
      )}

      {/* Squad Selection */}
      {showSquadSelection && (
        <FormControl fullWidth sx={{ mb: 2 }} required={squadRequired} error={!!errors.squad}>
          <InputLabel>{t('matches.squads')}</InputLabel>
          <Select value={squad || ''} label={t('matches.squads')} onChange={(e) => setSquad(e.target.value || null)}>
            {!squadRequired && <MenuItem value="">{t('regLanding.noPreference')}</MenuItem>}
            {squads.map((sq) => {
              const shooterCount = squadOccupancy(sq, { squadAvailability, shooters, editingShooter });
              const isFull = sq.maxShooters && shooterCount >= sq.maxShooters;
              return (
                <MenuItem key={sq.id} value={sq.id} disabled={isFull}>
                  {sq.title}
                  {sq.ro ? ` [${t('squads.roOnly')}]` : ''}
                  {sq.maxShooters ? ` (${shooterCount}/${sq.maxShooters})` : ` (${shooterCount})`}
                  {isFull ? ` - ${t('registration.noPlacesLeft')}` : ''}
                </MenuItem>
              );
            })}
          </Select>
          {errors.squad && <FormHelperText>{errors.squad}</FormHelperText>}
        </FormControl>
      )}

      {/* Team Selection */}
      {showTeamSelection && (
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>{t('shooter.team')}</InputLabel>
          <Select value={team || ''} label={t('shooter.team')} onChange={(e) => setTeam(e.target.value || null)}>
            <MenuItem value="">{t('regLanding.none')}</MenuItem>
            {teams.map((tm) => (
              <MenuItem key={tm.id} value={tm.id}>{tm.title}</MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {/* Extra Fields */}
      {extraFieldsDef.map((fieldKey) => {
        if (typeof fieldKey === 'object') {
          const id = fieldKey.id || fieldKey.name;
          return (
            <TextField
              key={id}
              label={fieldKey.label || fieldKey.name}
              value={extraFields[id] || ''}
              onChange={(e) => setExtraFields({ ...extraFields, [id]: e.target.value })}
              fullWidth
              required={fieldKey.required}
              sx={{ mb: 2 }}
            />
          );
        }
        const isOptional = fieldKey.endsWith('_optional');
        const baseKey = isOptional ? fieldKey.replace('_optional', '') : fieldKey;
        const fieldLabels = {
          shooter_address: t('shooter.address'),
          shooter_phone: t('shooter.phone'),
          shooter_social_number: t('shooter.socialNumber'),
          shooter_gun_license: t('shooter.gunLicense'),
          shooter_gun_license_exp: t('shooter.gunLicenseExp'),
          gun_make_and_model: t('shooter.gunMakeAndModel'),
          gun_serial: t('shooter.gunSerial'),
          gun_serial_2: t('shooter.gunSerial2', 'Second Gun Serial Number'),
          covid: t('manage.regCovid', 'Covid-19'),
          image: t('shooter.image'),
          equipment: t('equipment', 'Equipment Belt'),
        };
        const label = fieldLabels[baseKey] || fieldKey;
        const displayLabel = isOptional ? `${label} (${t('optional', 'Optional')})` : label;

        if (baseKey === 'image') {
          const imageVal = extraFields[fieldKey];
          const isDataUrl = typeof imageVal === 'string' && imageVal.startsWith('data:');
          return (
            <Box key={fieldKey} sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                {displayLabel}{!isOptional && ' *'}
              </Typography>
              <Button variant="outlined" component="label" size="small">
                {imageVal ? t('registration.changePhoto', 'Change Photo') : t('registration.uploadPhoto', 'Upload Photo')}
                <input type="file" hidden accept="image/*" onChange={(e) => {
                  const file = e.target.files[0];
                  if (!file) return;
                  const reader = new FileReader();
                  reader.onload = () => setExtraFields({ ...extraFields, [fieldKey]: reader.result });
                  reader.readAsDataURL(file);
                }} />
              </Button>
              {imageVal && (
                (isDataUrl || imageVal.startsWith('http'))
                  ? <Box component="img" src={imageVal} sx={{ display: 'block', mt: 1, maxWidth: 120, maxHeight: 120, borderRadius: 1 }} />
                  : <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>{imageVal}</Typography>
              )}
            </Box>
          );
        }

        if (baseKey === 'covid') {
          return (
            <FormControl key={fieldKey} fullWidth sx={{ mb: 2 }} required={!isOptional} error={!!errors[`extra_${fieldKey}`]}>
              <InputLabel>{displayLabel}</InputLabel>
              <Select value={extraFields[fieldKey] || ''} label={displayLabel} onChange={(e) => setExtraFields({ ...extraFields, [fieldKey]: e.target.value })}>
                <MenuItem value="vacinated">{t('registration.covidVacinated', 'Vaccinated')}</MenuItem>
                <MenuItem value="immuned">{t('registration.covidImmuned', 'Immuned / Recovered')}</MenuItem>
                <MenuItem value="tested">{t('registration.covidTested', 'Tested')}</MenuItem>
              </Select>
              {errors[`extra_${fieldKey}`] && <FormHelperText>{errors[`extra_${fieldKey}`]}</FormHelperText>}
            </FormControl>
          );
        }

        if (baseKey === 'equipment') {
          const equipmentObj = (() => {
            try { return JSON.parse(extraFields[fieldKey] || '{}'); } catch { return {}; }
          })();
          return (
            <EquipmentBelt
              key={fieldKey}
              equipmentObj={equipmentObj}
              onChange={(obj) => setExtraFields({ ...extraFields, [fieldKey]: JSON.stringify(obj) })}
            />
          );
        }

        return (
          <TextField
            key={fieldKey}
            label={displayLabel}
            value={extraFields[fieldKey] || ''}
            onChange={(e) => setExtraFields({ ...extraFields, [fieldKey]: e.target.value })}
            fullWidth
            required={!isOptional}
            error={!!errors[`extra_${fieldKey}`]}
            helperText={errors[`extra_${fieldKey}`]}
            sx={{ mb: 2 }}
          />
        );
      })}

      {/* Terms */}
      {showTos && (
        <>
          <FormControlLabel
            control={<Checkbox checked={acceptTos} onChange={(e) => setAcceptTos(e.target.checked)} />}
            label={tosLabel}
            sx={{ mb: 2 }}
          />
          {errors.tos && <FormHelperText error>{errors.tos}</FormHelperText>}
        </>
      )}

      {beforeSubmit}

      {/* Submit */}
      <Button
        variant="contained"
        color="primary"
        fullWidth
        size="large"
        onClick={handleSubmit}
        disabled={saving}
      >
        {saving ? <CircularProgress size={24} /> : (submitLabel || t('registration.register'))}
      </Button>
    </Wrapper>
  );

  return formContent;
});

export default ShooterForm;
